/**
 * Load the table's pictures before the first hand is shown.
 *
 * The first recording opened on green discs where the portraits should have been and a
 * blank white card where the deck was — the 1.8 MB printed back had not arrived. So
 * before the first `hand_start` is presented, every image the table will draw in its
 * first seconds is asked for: the portraits of the seats at this table, the card back
 * and the compact face stock, every chip piece and top, the plate and the disc. The
 * court and pip sheets are not on the list — they are many files, the first face-up
 * card is seconds away, and a wait for them is a wait the player would notice.
 *
 * Bounded by `preloadCeiling` (3 s): a slow image never holds a hand hostage. The
 * promise resolves either way; the caller deals when it does.
 */
import { avatarUrlForName } from "../art/roster";
import { CHIP_COLORS } from "../art/chips";
import { MOTION } from "./pace";

/*
 * PHASE 2B: preload the back the player is actually using.
 *
 * Two backs ship now, and preloading the red one for somebody playing with the plum one
 * is the bug this module exists to prevent — Phase 3b's "blank white card" was exactly
 * a back that had not arrived when the shuffle began.
 */
import { deckBackUrl } from "../settings/prefs";
const CARD_STOCK = "/art/cards/face@1x.png";
const PLATE = "/art/table/poker-topdown-1100.webp";
const DISC = "/art/ui/disc-ivory.webp";

/** Every URL the table needs first, for these seat names. */
export function tableImageUrls(names: readonly string[]): string[] {
  const urls = new Set<string>([deckBackUrl(), CARD_STOCK, PLATE, DISC]);
  for (const c of CHIP_COLORS) {
    urls.add(`/art/chips/top/${c}.webp`);
    for (const piece of ["cap", "slice", "tail"]) urls.add(`/art/chips/pieces/${c}-${piece}.png`);
  }
  for (const n of names) {
    const u = avatarUrlForName(n, "neutral");
    if (u) urls.add(u);
  }
  return [...urls];
}

function loadOne(url: string): Promise<void> {
  return new Promise((resolve) => {
    if (typeof Image === "undefined") { resolve(); return; }
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();     // a missing picture is the renderer's problem, not a reason to wait
    img.src = url;
    if (img.complete) resolve();
  });
}

/**
 * Resolves when every image has loaded or failed, or when the ceiling passes —
 * whichever is first. Never rejects.
 */
export function preloadTable(names: readonly string[], ceilingMs: number = MOTION.preloadCeiling): Promise<{ loaded: boolean; ms: number }> {
  const start = typeof performance !== "undefined" ? performance.now() : Date.now();
  const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now()) - start;
  const all = Promise.all(tableImageUrls(names).map(loadOne)).then(() => ({ loaded: true, ms: now() }));
  const cap = new Promise<{ loaded: boolean; ms: number }>((resolve) =>
    setTimeout(() => resolve({ loaded: false, ms: now() }), ceilingMs));
  return Promise.race([all, cap]);
}
